import { useEffect, useState } from 'react'
import { useProfitSettings, useUpdateProfitSettings } from '@/lib/useProfitSettings'
import { useToastStore } from '@/stores/toast'
import type { ProfitSettingsUpdate } from '@/types/profitSettings'

const DEFAULTS = { gain_50: 50, gain_100: 100, gain_200: 200, tax_free: 50 }

interface FormState {
  gain_50: number
  gain_100: number
  gain_200: number
  tax_free: number
}

function validate(state: FormState): Record<keyof FormState, string | null> {
  const e: Record<keyof FormState, string | null> = {
    gain_50: null,
    gain_100: null,
    gain_200: null,
    tax_free: null,
  }
  for (const key of Object.keys(state) as (keyof FormState)[]) {
    const v = state[key]
    if (!Number.isFinite(v) || v <= 0 || v >= 1000) {
      e[key] = 'Wert muss zwischen 0 und 1000 % liegen'
    }
  }
  if (state.gain_50 >= state.gain_100) {
    e.gain_50 ??= '+50 % muss kleiner als +100 % sein'
  }
  if (state.gain_100 >= state.gain_200) {
    e.gain_100 ??= '+100 % muss kleiner als +200 % sein'
  }
  return e
}

export function ThresholdsForm() {
  const query = useProfitSettings()
  const mutation = useUpdateProfitSettings()
  const pushToast = useToastStore((s) => s.pushToast)
  const [state, setState] = useState<FormState>(DEFAULTS)

  useEffect(() => {
    if (query.data) {
      setState({
        gain_50: Number(query.data.gain_50) * 100,
        gain_100: Number(query.data.gain_100) * 100,
        gain_200: Number(query.data.gain_200) * 100,
        tax_free: Number(query.data.tax_free) * 100,
      })
    }
  }, [query.data])

  const errors = validate(state)
  const hasError = Object.values(errors).some((e) => e !== null)
  const canSubmit = !hasError && !mutation.isPending

  if (query.isLoading) {
    return <p className="font-mono text-sm text-muted">Lade Einstellungen …</p>
  }

  const set = (k: keyof FormState) => (v: string) =>
    setState((prev) => ({ ...prev, [k]: Number(v) }))

  function submitChanges() {
    if (!canSubmit || !query.data) return
    const patch: ProfitSettingsUpdate = {}
    if (Number(query.data.gain_50) * 100 !== state.gain_50) {
      patch.gain_50 = (state.gain_50 / 100).toString()
    }
    if (Number(query.data.gain_100) * 100 !== state.gain_100) {
      patch.gain_100 = (state.gain_100 / 100).toString()
    }
    if (Number(query.data.gain_200) * 100 !== state.gain_200) {
      patch.gain_200 = (state.gain_200 / 100).toString()
    }
    if (Number(query.data.tax_free) * 100 !== state.tax_free) {
      patch.tax_free = (state.tax_free / 100).toString()
    }
    if (Object.keys(patch).length === 0) {
      pushToast({ kind: 'info', message: 'Keine Änderungen' })
      return
    }
    mutation.mutate(patch, {
      onSuccess: () => pushToast({ kind: 'success', message: 'Schwellen gespeichert' }),
      onError: (err) =>
        pushToast({
          kind: 'error',
          message: 'Speichern fehlgeschlagen',
          detail: (err as Error).message,
        }),
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    submitChanges()
  }

  return (
    <form onSubmit={handleSubmit} className="border border-hairline bg-panel px-6 py-6">
      <button type="submit" className="sr-only" aria-hidden="true" tabIndex={-1} />
      <Field
        label="+50 %-Trigger"
        hint="25 % Teilverkauf erwägen"
        value={state.gain_50}
        error={errors.gain_50}
        onChange={set('gain_50')}
      />
      <Field
        label="+100 %-Trigger"
        hint="weitere 25 % Teilverkauf"
        value={state.gain_100}
        error={errors.gain_100}
        onChange={set('gain_100')}
      />
      <Field
        label="+200 %-Trigger"
        hint="auf Ziel-Allokation rebalancen"
        value={state.gain_200}
        error={errors.gain_200}
        onChange={set('gain_200')}
      />
      <Field
        label="Tax-Free + Gewinn"
        hint="proaktive Empfehlung bei Steuerfreiheit"
        value={state.tax_free}
        error={errors.tax_free}
        onChange={set('tax_free')}
      />

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          className="tracked-caps px-4 py-3 text-[11px] text-muted transition-colors hover:text-ink"
          onClick={() => setState(DEFAULTS)}
        >
          Spec-Default
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          className="tracked-caps bg-bernstein px-6 py-3 text-[11px] text-void transition-colors hover:bg-bernstein-dim disabled:cursor-not-allowed disabled:bg-faint disabled:text-void/60"
          onClick={submitChanges}
        >
          Speichern
        </button>
      </div>

      {mutation.isError && (
        <p role="alert" className="mt-3 font-mono text-xs text-loss">
          Speichern fehlgeschlagen: {(mutation.error as Error).message}
        </p>
      )}
    </form>
  )
}

interface FieldProps {
  label: string
  hint: string
  value: number
  error: string | null
  onChange: (v: string) => void
}

function Field({ label, hint, value, error, onChange }: FieldProps) {
  const id = `field-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div className="grid grid-cols-[1fr_80px] items-center gap-3 border-b border-hairline py-3 md:grid-cols-[1fr_120px_60px]">
      <div className="col-span-2 md:col-span-1">
        <label htmlFor={id} className="text-ink">
          {label}
        </label>
        <p className="font-mono text-xs text-muted">{hint}</p>
        {error && (
          <p role="alert" className="mt-1 font-mono text-xs text-loss">
            {error}
          </p>
        )}
      </div>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={0.01}
        max={999}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-hairline bg-void px-3 py-2 text-right font-mono text-sm text-ink transition-colors focus:border-bernstein focus:outline-none"
      />
      <span className="font-mono text-xs text-faint">% Gewinn</span>
    </div>
  )
}
